import type { FuturesMarketAsset, OrderTypeEnum } from '@kwenta/sdk/types'
import { formatDollars, formatPercent, getDefaultPriceImpactDeltaPercent } from '@kwenta/sdk/utils'
import type Wei from '@kwenta/wei'
import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { InfoBoxRow } from 'components/InfoBox'
import NumericInput from 'components/Input/NumericInput'
import { Body } from 'components/Text'
import { NO_VALUE } from 'constants/placeholder'
import { selectSlippageInput } from 'state/futures/common/selectors'
import { editSmartMarginTradeSlippage } from 'state/futures/isolatedMargin/actions'
import {
	selectIsIsolatedMarginProvider,
	selectTradeSizeInputsDisabled,
} from 'state/futures/selectors'
import { editCrossMarginTradeSlippage } from 'state/futures/snxPerpsV3/actions'
import { useAppDispatch, useAppSelector } from 'state/hooks'

export const LiquidationRow = memo(
	({ liqPrice, showData }: { liqPrice?: string | undefined; showData: boolean }) => {
		return (
			<InfoBoxRow
				title="Est. Liquidation price"
				textValue={
					liqPrice && showData ? formatDollars(liqPrice, { suggestDecimals: true }) : NO_VALUE
				}
			/>
		)
	}
)

export const PriceImpactRow = memo(
	({ priceImpact, showData }: { priceImpact: Wei | undefined; showData: boolean }) => {
		return (
			<InfoBoxRow
				title="Price impact"
				textValue={priceImpact && showData ? formatPercent(priceImpact) : NO_VALUE}
			/>
		)
	}
)

export const FillPriceRow = memo(
	({
		fillPrice,
		asset,
		showData,
	}: {
		fillPrice: Wei | undefined
		showData: boolean
		asset?: FuturesMarketAsset
	}) => {
		return (
			<InfoBoxRow
				title="Fill Price"
				textValue={
					fillPrice && showData
						? formatDollars(fillPrice, { suggestDecimalsForAsset: asset })
						: NO_VALUE
				}
			/>
		)
	}
)

const SlippageInputContainer = styled.div`
	width: 100%;
	height: 22px;
	max-width: 56px;
`
const SuffixLable = styled(Body)`
	color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.secondary};
`

type Props = {
	orderType: OrderTypeEnum
}

export const SlippageRow = memo(({ orderType }: Props) => {
	const dispatch = useAppDispatch()
	const slippageInput = useAppSelector(selectSlippageInput)
	const isIsolated = useAppSelector(selectIsIsolatedMarginProvider)
	const defaultSlippage = useMemo(() => getDefaultPriceImpactDeltaPercent(orderType), [orderType])
	const isDisabled = useAppSelector(selectTradeSizeInputsDisabled)

	const onSlippageChange = useCallback(
		(newSlippage: string) => {
			if (isIsolated) {
				dispatch(editSmartMarginTradeSlippage(newSlippage))
			} else {
				dispatch(editCrossMarginTradeSlippage(newSlippage))
			}
		},
		[dispatch, isIsolated]
	)
	return (
		<InfoBoxRow
			title="Max Slippage"
			nodeValue={
				<SlippageInputContainer>
					<NumericInput
						data-testid="slippage-input"
						value={slippageInput}
						placeholder={defaultSlippage}
						right={<SuffixLable>%</SuffixLable>}
						onChange={(_, newValue) => {
							onSlippageChange(newValue)
						}}
						disabled={isDisabled}
						maxLength={4}
						roundSize={3}
						height={22}
						fontSize={'medium'}
					/>
				</SlippageInputContainer>
			}
		/>
	)
})
